import { Activity, CircleCheck, TriangleAlert } from "lucide-react";
import { useEffect, useReducer } from "react";

import { StateLabel, stateTextClass } from "../ui";
import { useWs } from "../ws";

type Running = {
	storeId: string;
	startedAt: number;
	manual: boolean;
	baseline: boolean;
	pages: number;
	items: number;
};

type Toast = {
	id: string;
	storeId: string;
	/** "short": the sweep finished but lost products, and `detail` says why. */
	status: "ok" | "short" | "fail";
	detail: string;
	shownAt: number;
};

export type SweepBannerState = {
	running: Record<string, Running>;
	toasts: Toast[];
};

export const emptySweepState: SweepBannerState = { running: {}, toasts: [] };

type Action =
	| {
			type: "started";
			storeId: string;
			manual: boolean;
			baseline: boolean;
			startedAt: number;
	  }
	| { type: "progress"; storeId: string; pages: number; items: number }
	| {
			type: "complete";
			storeId: string;
			ingested: number;
			pages: number;
			durationMs: number;
			truncated: boolean;
			problem?: string | null;
	  }
	| { type: "failed"; storeId: string; error: string; durationMs: number }
	| { type: "expire-toast"; before: number };

const TOAST_TTL_MS = 6_000;

/**
 * Which sweeps are worth interrupting the page for.
 *
 * Everything used to be: each store announced that it had started and again
 * that it had finished, as cards in the page flow. With eleven stores on
 * their own cadences that meant stacks of "138 ingested · 1p · 0.7s"
 * appearing and expiring six seconds later, shoving the feed down and
 * snapping it back — for the machine reporting that it had done its job.
 *
 * A routine scheduled sweep is now silent. The health line above already says
 * when each store last ran, and Manage > Stores keeps a per-store column. A
 * manual sweep still reports, because someone pressed a button and is owed an
 * answer, and failures always report. Nothing else does.
 */
export function sweepReducer(state: SweepBannerState, action: Action): SweepBannerState {
	switch (action.type) {
		case "started":
			if (!action.manual) return state;
			return {
				...state,
				running: {
					...state.running,
					[action.storeId]: {
						storeId: action.storeId,
						startedAt: action.startedAt,
						manual: action.manual,
						baseline: action.baseline,
						pages: 0,
						items: 0,
					},
				},
			};
		case "progress": {
			const r = state.running[action.storeId];
			if (!r) return state;
			return {
				...state,
				running: {
					...state.running,
					[action.storeId]: { ...r, pages: action.pages, items: action.items },
				},
			};
		}
		case "complete": {
			const { [action.storeId]: dropped, ...rest } = state.running;
			// `running` only holds manual sweeps, so its presence is the signal.
			// Truncation is deliberately not a reason on its own: a store above the
			// page ceiling truncates on every sweep, so it would produce a card
			// every couple of minutes for a condition that holds until someone
			// narrows its categories. Manage > Stores marks that persistently.
			if (!dropped?.manual) return { ...state, running: rest };
			return {
				running: rest,
				toasts: [
					...state.toasts,
					{
						id: `${action.storeId}:${Date.now()}`,
						storeId: action.storeId,
						status: action.problem ? "short" : "ok",
						detail: action.problem
							? `${action.ingested} ingested · ${action.problem}`
							: `${action.ingested} ingested · ${action.pages}p · ` +
								`${(action.durationMs / 1000).toFixed(1)}s` +
								// A sweep that stopped at the ceiling read only part of the
								// store, and looks identical to a finished one without this.
								(action.truncated ? " · stopped at page limit" : ""),
						shownAt: Date.now(),
					},
				],
			};
		}
		case "failed": {
			const { [action.storeId]: _drop, ...rest } = state.running;
			return {
				running: rest,
				toasts: [
					...state.toasts,
					{
						id: `${action.storeId}:${Date.now()}`,
						storeId: action.storeId,
						status: "fail",
						detail: action.error,
						shownAt: Date.now(),
					},
				],
			};
		}
		case "expire-toast":
			return {
				...state,
				toasts: state.toasts.filter((t) => t.shownAt > action.before),
			};
	}
}

export function SweepBanner() {
	const [state, dispatch] = useReducer(sweepReducer, emptySweepState);

	useWs((msg) => {
		if (msg.type === "sweep.started") {
			dispatch({
				type: "started",
				storeId: msg.storeId,
				manual: msg.manual,
				baseline: msg.baseline,
				startedAt: msg.startedAt,
			});
		} else if (msg.type === "sweep.progress") {
			dispatch({
				type: "progress",
				storeId: msg.storeId,
				pages: msg.pages,
				items: msg.items,
			});
		} else if (msg.type === "sweep.complete") {
			dispatch({
				type: "complete",
				storeId: msg.storeId,
				ingested: msg.ingested,
				pages: msg.pages,
				durationMs: msg.durationMs,
				truncated: msg.truncated,
				problem: msg.problem,
			});
		} else if (msg.type === "sweep.failed") {
			dispatch({
				type: "failed",
				storeId: msg.storeId,
				error: msg.error,
				durationMs: msg.durationMs,
			});
		}
	});

	useEffect(() => {
		if (state.toasts.length === 0) return;
		const t = setInterval(() => {
			dispatch({ type: "expire-toast", before: Date.now() - TOAST_TTL_MS });
		}, 1000);
		return () => clearInterval(t);
	}, [state.toasts.length]);

	const running = Object.values(state.running);
	if (running.length === 0 && state.toasts.length === 0) return null;

	return (
		<div className="mb-3 flex flex-col gap-1.5">
			{running.map((r) => (
				<div
					key={r.storeId}
					className="flex flex-wrap items-center gap-2 rounded-[10px] border border-hairline bg-paper px-3 py-1.5 text-xs text-ink"
				>
					<Activity className="size-3.5 animate-pulse text-muted" aria-hidden />
					<span className="font-medium">sweeping {r.storeId}</span>
					<span className="text-[11px] text-muted">
						{r.manual ? "manual" : "scheduled"}
						{r.baseline ? " · baseline" : ""}
					</span>
					<span className="ml-auto text-muted">
						{r.pages}p · {r.items} items
					</span>
				</div>
			))}
			{state.toasts.map((t) =>
				t.status === "ok" ? (
					<div
						key={t.id}
						className="flex flex-wrap items-center gap-2 rounded-[10px] border border-hairline bg-paper px-3 py-1.5 text-xs text-ink"
					>
						<CircleCheck className="size-3.5 text-ink" aria-hidden />
						<span className="font-medium">{t.storeId}</span>
						<span className="ml-auto min-w-0 truncate text-[11px]" title={t.detail}>
							{t.detail}
						</span>
					</div>
				) : (
					<div
						key={t.id}
						className="flex flex-wrap items-center gap-2 rounded-[10px] border border-hairline bg-paper px-3 py-1.5 text-xs"
					>
						<TriangleAlert
							className={`size-3.5 ${stateTextClass(t.status === "short" ? "needs" : "fault")}`}
							aria-hidden
						/>
						<span className="font-medium">{t.storeId}</span>
						<StateLabel
							state={t.status === "short" ? "needs" : "fault"}
							label={t.status === "short" ? "Came up short" : "Failed"}
						/>
						{/* Ordinary prose, not StateLabel: this is free-form worker/HTTP
                error text (URLs, mixed case), and StateLabel's one-line label
                mono would mangle it. No truncate — the operator needs to
                read it verbatim; the flex-wrap on the row above lets it wrap
                onto its own line instead of overflowing. */}
						<span className="ml-auto min-w-0 text-[11px] break-words text-muted">{t.detail}</span>
					</div>
				),
			)}
		</div>
	);
}
